import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';

type Tab = 'login' | 'register';

function PendingScreen({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
      <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
        <i className="ri-time-line text-amber-500 text-2xl" />
      </div>
      <h2 className="text-lg font-bold text-[#1a2e4a] mb-2">Registration Pending</h2>
      <p className="text-sm text-stone-500 leading-relaxed mb-5">
        Your account has been submitted and is awaiting approval from the administrator.
        You will be able to access the portal once your account is approved.
      </p>
      <button
        onClick={onSignOut}
        className="text-sm text-stone-400 hover:text-stone-600 transition-colors cursor-pointer underline underline-offset-2"
      >
        Sign out
      </button>
    </div>
  );
}

function LoginForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const { error: authErr } = await supabase.auth.signInWithPassword({ email, password });
    if (authErr) {
      setError('Invalid email or password. Please try again.');
      setSubmitting(false);
      return;
    }
    // Check profile after login
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_approved, role')
        .eq('id', user.id)
        .maybeSingle();
      if (profile && profile.is_approved) {
        navigate('/partner-portal', { replace: true });
        return;
      }
      // isPending will be handled by usePartnerAuth - stay on page
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleLogin} className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Password</label>
        <div className="relative">
          <input
            type={showPass ? 'text' : 'password'}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPass(!showPass)}
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600"
          >
            <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
          </button>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#1a2e4a] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#253f62] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
      >
        {submitting ? 'Signing in...' : 'Sign In'}
      </button>
    </form>
  );
}

function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'partner' as 'manager' | 'partner',
    org_name: '',
    notes: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    // Create auth user + profile via Edge Function (no confirmation email)
    const res = await fetch(
      'https://glleucmaurrrorovhoru.supabase.co/functions/v1/create-user-profile',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          role: form.role,
          full_name: form.full_name.trim(),
          org_name: form.org_name.trim() || null,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      setError(data.error || 'Registration failed. Please try again.');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Full Name *</label>
        <input
          type="text"
          required
          value={form.full_name}
          onChange={e => set('full_name', e.target.value)}
          placeholder="Your full name"
          className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Email *</label>
        <input
          type="email"
          required
          value={form.email}
          onChange={e => set('email', e.target.value)}
          placeholder="your@email.com"
          className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Password *</label>
        <div className="relative">
          <input
            type={showPass ? 'text' : 'password'}
            required
            value={form.password}
            onChange={e => set('password', e.target.value)}
            placeholder="Min. 6 characters"
            className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all pr-10"
          />
          <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600">
            <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Confirm Password *</label>
        <input
          type="password"
          required
          value={form.confirm_password}
          onChange={e => set('confirm_password', e.target.value)}
          placeholder="Repeat password"
          className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">Account Type *</label>
        <div className="flex gap-2">
          {(['partner', 'manager'] as const).map(r => (
            <button
              key={r}
              type="button"
              onClick={() => set('role', r)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg border transition-all cursor-pointer whitespace-nowrap capitalize ${form.role === r ? 'bg-[#1a2e4a] text-white border-[#1a2e4a]' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}
            >
              {r === 'partner' ? 'Partner (NGO)' : 'Manager'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">
          Organization Name {form.role === 'partner' ? '*' : '(optional)'}
        </label>
        <input
          type="text"
          required={form.role === 'partner'}
          value={form.org_name}
          onChange={e => set('org_name', e.target.value)}
          placeholder="Name of your NGO"
          className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1a2e4a]/20 focus:border-[#1a2e4a] transition-all"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#1a2e4a] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#253f62] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap mt-1"
      >
        {submitting ? 'Submitting...' : 'Submit Registration'}
      </button>
    </form>
  );
}

function SuccessScreen({ onBackToLogin }: { onBackToLogin: () => void }) {
  return (
    <div className="text-center py-4">
      <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
        <i className="ri-check-line text-emerald-500 text-2xl" />
      </div>
      <h2 className="text-lg font-bold text-[#1a2e4a] mb-2">Registration Submitted!</h2>
      <p className="text-sm text-stone-500 leading-relaxed mb-5">
        Your request has been sent to the administrator for review.
        Once your account is approved, you will be able to sign in.
      </p>
      <button
        onClick={onBackToLogin}
        className="text-sm font-semibold text-[#1a2e4a] hover:underline cursor-pointer"
      >
        Back to Sign In
      </button>
    </div>
  );
}

export default function PartnerAccessPage() {
  const navigate = useNavigate();
  const { profile, isPending, loading, logout } = usePartnerAuth();
  const [tab, setTab] = useState<Tab>('login');
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    if (!loading && profile) navigate('/partner-portal', { replace: true });
  }, [profile, loading, navigate]);

  const handleSignOut = async () => {
    await logout();
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="w-8 h-8 border-2 border-[#1a2e4a] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img
              src="https://storage.readdy-site.link/project_files/d916de51-3f57-462f-a700-620facc3e09c/96d60ca8-e1eb-45a0-b27f-a662d0ca5b1e_EU-LOGO-W1.png?v=85a6b8a22d2db99c2b056706e2aee0e2"
              alt="EU Next Grants"
              className="h-14 w-auto mx-auto"
              style={{ filter: 'invert(1)' }}
            />
          </Link>
          <h1 className="text-2xl font-bold text-[#1a2e4a] tracking-tight">Partner Portal</h1>
          <p className="text-sm text-stone-500 mt-1">NGO Catalog — Restricted Access</p>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-8">
          {/* Pending state */}
          {isPending ? (
            <PendingScreen onSignOut={handleSignOut} />
          ) : registered ? (
            <SuccessScreen onBackToLogin={() => { setRegistered(false); setTab('login'); }} />
          ) : (
            <>
              {/* Tab switcher */}
              <div className="flex items-center bg-stone-100 rounded-lg p-1 mb-6">
                {(['login', 'register'] as Tab[]).map(t => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap capitalize ${tab === t ? 'bg-white text-[#1a2e4a] shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                  >
                    {t === 'login' ? 'Sign In' : 'Register'}
                  </button>
                ))}
              </div>

              {tab === 'login' ? (
                <LoginForm />
              ) : (
                <RegisterForm onSuccess={() => setRegistered(true)} />
              )}
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1" />
            Back to EU Next Grants
          </Link>
        </div>
      </div>
    </div>
  );
}
